import { FormEvent, useMemo, useReducer, useState } from "react";
import * as Form from "@radix-ui/react-form";
import toast from "react-hot-toast";
import { UserRepository } from "@amityco/ts-sdk";

import {
  Button,
  CheckboxInput,
  ColumnModal,
  Input,
  UserAvatar,
  UnsavedChangesModal,
} from "@/components";
import { PatchUser } from "@/api";
import { useAppProperties, useUserDetails } from "@/hooks";
import { ChevronLeft, CloudUpload, Delete } from "@/icons";
import { useAmityAuth, useTranslate } from "@/providers/context";
import { Header } from "@/app/modules/Navigation";
import { resizeImage } from "@/utils";

import { Section } from "./components/Section.tsx";
import { Separator } from "./components/Separator.tsx";
import { useUpdateUser } from "./hooks/useUpdateUser.tsx";
import { useNavigate } from "react-router-dom";
import { uploadMedia } from "../Community/util.tsx";

type DirtyFieldsState = {
  [key: string]: boolean;
};

type DirtyFieldsAction =
  | { type: "SET_DIRTY"; field: string }
  | { type: "RESET" };
// Fields that can be send to PATCH request
const FORM_FIELDS = [
  "FirstName",
  "LastName",
  "Email",
  "MarketingOptIn",
] as const;
// Set a field as dirty
const dirtyFieldsReducer = (
  state: DirtyFieldsState,
  action: DirtyFieldsAction,
): DirtyFieldsState => {
  switch (action.type) {
    case "SET_DIRTY":
      return { ...state, [action?.field]: true };
    case "RESET":
      return {};
    default:
      throw new Error(`Unhandled action: ${action}`);
  }
};

export const ProfileUpdateScreen = () => {
  const { translate } = useTranslate();
  const { data } = useUserDetails();
  const updateUser = useUpdateUser();
  const [apiError, setApiError] = useState("");
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const navigate = useNavigate();
  const { authState } = useAmityAuth();
  const { communityEnabled } = useAppProperties();

  const {
    existingFirstName,
    existingLastName,
    existingEmail,
    existingAvatarURL,
    existingOptIn,
  } = useMemo(() => {
    return {
      existingFirstName: data?.first_name ?? "",
      existingLastName: data?.last_name ?? "",
      existingEmail: data?.email,
      existingAvatarURL: data?.avatar_url ?? "",
      existingOptIn: data?.marketing_opt_in ?? false,
    };
  }, [data]);

  // Store an object of fields that have been edited e.g { FirstName: true}
  const [dirtyFields, dispatch] = useReducer(dirtyFieldsReducer, {});
  // Store pending form submission when confirming email change
  const [pendingSubmission, setPendingSubmission] = useState<
    PatchUser | undefined
  >(undefined);
  const [password, setPassword] = useState<string>("");
  const [newOptIn, setNewOptIn] = useState<boolean | undefined>(undefined);
  const [amityIsSaving, setAmityIsSaving] = useState(false);

  const handleConfirmNavigation = () => {
    setUnsavedOpen(false);
    navigate(-1);
  };

  const handleCancelNavigation = () => {
    setUnsavedOpen(false); // Close the modal
  };

  // Undefined - means use existing avatar
  // "" means we are deleting the existing avatar
  // Any other value  wil be the Base64 string of the new avatar
  const [newAvatar, setNewAvatar] = useState<string | undefined>(undefined);
  // As File for use by Amity
  const [newAvatarFile, setNewAvatarFile] = useState<File | undefined>(
    undefined,
  );

  const handleFieldChange = (
    field: string,
    value: string | boolean,
    initialValue: string | boolean,
  ) => {
    if (value !== initialValue) {
      dispatch({ type: "SET_DIRTY", field });
    }
  };

  const isEmailEdited = !!dirtyFields["Email"];

  const handleAvatarUpload = async (file: File) => {
    const { resizedFile, base64 } = await resizeImage(file, 400, 400);
    setNewAvatarFile(resizedFile);
    setNewAvatar(base64);
  };

  const handleRemoveAvatar = () => {
    if (newAvatar) {
      setNewAvatar(undefined); // Reset the newly uploaded avatar
    } else if (existingAvatarURL) {
      setNewAvatar(""); // Indicate the avatar is removed
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = Object.fromEntries(new FormData(event.currentTarget));

    const payload: Record<string, FormDataEntryValue> = {};
    for (const field of FORM_FIELDS) {
      if (dirtyFields[field] && formData[field] !== undefined) {
        payload[field] = formData[field];
      }
    }

    // Include Avatar updates in the submission
    if (newAvatar === "") {
      payload["AvatarURL"] = "";
    } else if (newAvatar) {
      // Strip the data:image/png;base64 prefix from the uploaded image
      payload["AvatarIcon"] = newAvatar.split(",")[1];
    }

    const updatedAmityFields: Record<string, string> = {};

    if (payload["FirstName"] || payload["LastName"]) {
      const amityDisplayName = `${formData["FirstName"] ?? ""} ${formData["LastName"] ?? ""}`;
      updatedAmityFields.displayName = amityDisplayName;
    }

    if (newAvatarFile) {
      setAmityIsSaving(true);
      try {
        const uploadedImage = await uploadMedia(newAvatarFile, () => {});
        updatedAmityFields.avatarFileId = uploadedImage.fileId;
      } catch (error) {
        console.error("Error uploading avatar to amity:", error);
      }
    }

    if (communityEnabled && Object.keys(updatedAmityFields).length > 0) {
      if (
        authState.state === "success" &&
        authState.authenticatedUser?.userId
      ) {
        try {
          setAmityIsSaving(true);
          await UserRepository.updateUser(authState.authenticatedUser.userId, {
            ...updatedAmityFields,
          });
        } catch (error) {
          console.error("Error updating amity user:", error);
        } finally {
          setAmityIsSaving(false);
          setNewAvatarFile(undefined);
        }
      }
    }

    if (!isEmailEdited) {
      updateUser.mutate(payload as unknown as PatchUser, {
        onSuccess: () => {
          dispatch({ type: "RESET" });
          toast(translate("Profile details successfully updated"));
        },
        onError: (error) => {
          toast(translate(error.message));
        },
      });
    } else {
      setPendingSubmission(payload as unknown as PatchUser);
    }
  };

  const imageUrl = useMemo(() => {
    if (newAvatar === "") {
      return undefined;
    }

    return newAvatar ?? existingAvatarURL;
  }, [newAvatar, existingAvatarURL]);

  return (
    <div className="px-7">
      <Header
        content={
          <div>
            <Button
              size="m"
              className="p2-semibold ml-[-4px]" // Make it flush with left aligned content
              variant="tertiary"
              icon={<ChevronLeft className="h-[20px] w-[20px]" />}
              iconPosition="left"
              onClick={() => {
                if (Object.keys(dirtyFields)?.length > 0) {
                  setUnsavedOpen(true);
                } else {
                  navigate(-1);
                }
              }}
            >
              {translate("Back")}
            </Button>
          </div>
        }
      />
      <div className="flex justify-center">
        <div className="flex w-[47%] max-w-[624px] flex-col items-center">
          <Form.Root className="w-full" onSubmit={handleSubmit}>
            <Section heading={translate("Profile Image")}>
              <div className="flex items-center gap-7">
                <UserAvatar
                  className="h-[108px] w-[108px] text-4xl"
                  name={`${existingFirstName} ${existingLastName}`}
                  imageUrl={imageUrl}
                />
                <div>
                  <div className="flex gap-1">
                    <Button
                      variant="secondary"
                      size="s"
                      icon={<CloudUpload />}
                      iconPosition="left"
                      onClick={() => {
                        const fileInput = document.createElement("input");
                        fileInput.type = "file";
                        fileInput.accept = "image/png, image/jpeg";
                        fileInput.onchange = (event) => {
                          const file = (event.target as HTMLInputElement)
                            .files?.[0];
                          if (file) {
                            handleAvatarUpload(file);
                          }
                        };
                        fileInput.click();
                      }}
                    >
                      {translate("Upload Image")}
                    </Button>
                    {imageUrl && (
                      <Button
                        size="s"
                        variant="tertiary"
                        className="!text-error-item"
                        icon={<Delete />}
                        iconPosition="left"
                        onClick={handleRemoveAvatar}
                      >
                        {translate("Remove Image")}
                      </Button>
                    )}
                  </div>
                  <div className="caption-normal mt-2 text-item-secondary">
                    {translate("Recommended 400x400px in PNG or JPG format")}
                  </div>
                </div>
              </div>
            </Section>
            <div>
              <div className="flex">
                <div className="w-1/2 pr-5">
                  <h4 className="p2-bold pb-5 text-item-secondary">
                    {translate("First Name")}
                  </h4>
                  <Form.Field name="FirstName">
                    <Form.Control asChild required>
                      <Input
                        type="text"
                        placeholder=""
                        defaultValue={existingFirstName}
                        onChange={(e) =>
                          handleFieldChange(
                            "FirstName",
                            e.target.value,
                            existingFirstName,
                          )
                        }
                      />
                    </Form.Control>
                    <Form.Message
                      className="mt-2 block text-error-item"
                      match="valueMissing"
                    >
                      {translate("Please enter a first name")}
                    </Form.Message>
                  </Form.Field>
                </div>
                <div className="w-1/2">
                  <h4 className="p2-bold pb-5 text-item-secondary">
                    {translate("Last Name")}
                  </h4>
                  <Form.Field name="LastName">
                    <Form.Control asChild>
                      <Input
                        type="text"
                        placeholder=""
                        defaultValue={existingLastName}
                        onChange={(e) =>
                          handleFieldChange(
                            "LastName",
                            e.target.value,
                            existingLastName,
                          )
                        }
                      />
                    </Form.Control>
                  </Form.Field>
                </div>
              </div>
            </div>
            <Separator />
            <div className="w-full">
              <h4 className="p2-bold pb-5 text-item-secondary">
                {translate("Email")}
              </h4>
              <Form.Field name="Email">
                <Form.Control asChild required type="email">
                  <Input
                    defaultValue={existingEmail}
                    onChange={(e) =>
                      handleFieldChange(
                        "Email",
                        e.target.value,
                        existingEmail ?? "",
                      )
                    }
                  />
                </Form.Control>
                <Form.Message
                  className="mt-2 block text-error-item"
                  match="typeMismatch"
                >
                  {translate("Please enter a valid email address")}
                </Form.Message>
              </Form.Field>
            </div>
            <div className="flex w-full items-center gap-2 pt-4">
              <Form.Field name="MarketingOptIn">
                <Form.Control asChild required type="checkbox">
                  <CheckboxInput
                    id="opt-in"
                    name="MarketingOptIn"
                    checked={newOptIn ?? existingOptIn}
                    onCheckedChange={(value) => {
                      setNewOptIn(value);
                      handleFieldChange("MarketingOptIn", value, existingOptIn);
                    }}
                  />
                </Form.Control>
              </Form.Field>
              <h4 className="p2-medium text-item-secondary">
                {translate(
                  "Send me updates and information to keep me engaged",
                )}
              </h4>
            </div>
            <div className="mt-10 flex justify-end">
              <Form.Submit asChild>
                <Button
                  loading={updateUser.isLoading || amityIsSaving}
                  disabled={updateUser.isLoading || amityIsSaving}
                >
                  {translate("Save Changes")}
                </Button>
              </Form.Submit>
            </div>
          </Form.Root>
        </div>
      </div>
      <ColumnModal
        hideTrigger
        cols={3}
        open={!!pendingSubmission}
        onOpenChange={() => {
          setPendingSubmission(undefined);
          setApiError("");
          setPassword("");
        }}
        onCancel={() => {
          setPendingSubmission(undefined);
          setApiError("");
          setPassword("");
        }}
        onConfirm={() => {
          setApiError("");
          // This is always set
          if (pendingSubmission) {
            updateUser.mutate(
              { ...pendingSubmission, Secret: password },
              {
                onSuccess: () => {
                  setPendingSubmission(undefined);
                  dispatch({ type: "RESET" });
                  toast(translate("Profile details successfully updated"));
                },
                onError: (error: Error) => {
                  setApiError(error.message);
                },
              },
            );
          }
        }}
        confirmIsLoading={updateUser.isLoading}
        confirmDisabled={updateUser.isLoading || !password}
        title={translate("Check Your New Email Is Correct")}
        contentClassName="px-6"
        primaryText={translate("Confirm")}
        secondaryText={translate("Cancel")}
      >
        <p className="p1-regular">
          {translate(
            "Please check the below email is correct to ensure that you can receive any account related emails.",
          )}
        </p>
        <div className="pb-6 pt-2">
          <div className="p1-bold mb-4">{pendingSubmission?.Email}</div>
          <Input
            placeholder={translate("Enter your password to change email")}
            type="password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="p2-medium block h-[20px] py-2 text-error-item">
            {translate(apiError)}
          </div>
        </div>
      </ColumnModal>
      <UnsavedChangesModal
        open={unsavedOpen}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
      />
    </div>
  );
};
